import React, { useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import {
  Navigate,
  Outlet,
  useNavigate,
  Link,
  useLocation,
} from "react-router-dom";
import Loading from "../components/ui/Loading/Loading";
import { useEffect } from "react";
import { io } from "socket.io-client";
import axios from "axios";
import { queryClient } from "../App";
import chat_notification from "../assets/chat_notification.wav";
import notification_sweet from "../assets/notification_sweet.wav";
import { setOnlineUsers } from "../app/slices/soketSlice";
import { toast } from "react-hot-toast";
import Notification from "../components/ui/Notification/Notification";
import {
  logout,
  reciveNoti,
  updateNStats,
  updateUserInfo,
} from "../app/slices/userSlice";
import {
  updateChatName,
  updateChatTheme,
  updateMessages,
  updateSeenMessages,
} from "../utils/rqUpdate";
import { backendUrl, apiUrl } from "../utils/backendUrl";
import { get } from "../services/api";
import Topbar from "../components/layout/Topbar";
import SubNavbar from "../components/layout/SubNavbar";
import Footer from "../components/layout/Footer";
import ChatSidebar from "../components/layout/ChatSidebar";
import ChatPopupManager from "../components/chat/ChatPopupManager";
import { MiniChatProvider, miniChatBus } from "../context/MiniChatContext";
import { createRingtone } from "../utils/ringtone";
import {
  buildTenantRelativePath,
  buildTenantUserPath,
  isCustomTenantHost,
} from "../utils/tenantPreview";
import {
  getNotificationStream,
  NOTIFICATION_STREAMS,
} from "../utils/notificationStreams";

// ── Socket handle ───────────────────────────────────────────────────────
// `socket` is a *stable* module-level handle that is never reassigned. The
// underlying socket.io connection is swapped by `attachSocketInstance` when
// the signed-in identity changes, and every listener registered through this
// handle is moved to the new connection. That lets consumers subscribe once
// (typically a `[]`-dep effect) and stay subscribed for the life of their
// mount — previously they held a reference to a connection that had already
// been disconnected, and their cleanup `.off()` ran against a different
// instance. Registering before a connection exists is also safe: those
// listeners attach as soon as one does, which matters because child effects
// run before this route's effect on the very first paint.
let activeSocketInstance = null;
const socketListeners = new Map(); // event -> Set<handler>
const noopEmitter = { emit: () => undefined };

function eachSocketListener(visit) {
  socketListeners.forEach((handlers, event) => {
    handlers.forEach((handler) => visit(event, handler));
  });
}

function attachSocketInstance(instance) {
  const nextInstance = instance || null;
  if (activeSocketInstance === nextInstance) return;
  if (activeSocketInstance) {
    eachSocketListener((event, handler) =>
      activeSocketInstance.off(event, handler)
    );
  }
  activeSocketInstance = nextInstance;
  if (activeSocketInstance) {
    eachSocketListener((event, handler) =>
      activeSocketInstance.on(event, handler)
    );
  }
}

export const socket = {
  get connected() {
    return Boolean(activeSocketInstance?.connected);
  },
  get id() {
    return activeSocketInstance?.id;
  },
  get volatile() {
    return activeSocketInstance?.volatile || noopEmitter;
  },
  on(event, handler) {
    if (typeof handler !== "function") return socket;
    let handlers = socketListeners.get(event);
    if (!handlers) {
      handlers = new Set();
      socketListeners.set(event, handlers);
    }
    if (!handlers.has(handler)) {
      handlers.add(handler);
      activeSocketInstance?.on(event, handler);
    }
    return socket;
  },
  once(event, handler) {
    if (typeof handler !== "function") return socket;
    const wrapped = (...args) => {
      socket.off(event, wrapped);
      handler(...args);
    };
    return socket.on(event, wrapped);
  },
  off(event, handler) {
    const handlers = socketListeners.get(event);
    if (handler === undefined) {
      handlers?.forEach((registered) =>
        activeSocketInstance?.off(event, registered)
      );
      socketListeners.delete(event);
      return socket;
    }
    if (handlers?.delete(handler) && !handlers.size) {
      socketListeners.delete(event);
    }
    activeSocketInstance?.off(event, handler);
    return socket;
  },
  emit(...args) {
    activeSocketInstance?.emit(...args);
    return socket;
  },
  disconnect() {
    activeSocketInstance?.disconnect();
    return socket;
  },
};

const tenantWorkspaceShellSectionPattern =
  /^\/[^/]+\/(admin|goals|roles|subclans|bonds|flows|reports|search|friends|usage|support|notes|calendar|events|alerts|notifications|connections|messages|conversations)(?:\/|$)/;
const customWorkspaceShellSectionPattern =
  /^\/(admin|goals|roles|subclans|bonds|flows|reports|search|friends|usage|support|notes|calendar|events|alerts|notifications|connections|messages|conversations)(?:\/|$)/;
const tenantCommerceWorkspacePattern = /^\/[^/]+\/commerce(?:\/|$)/;
const customCommerceWorkspacePattern = /^\/commerce(?:\/|$)/;
const redesignedSubclanTabPattern =
  "(?:alerts|notifications|calendar|events|connections|bonds|members|requests|messages|chat|conversations|notes|notepad)";
const tenantSubclanWorkspacePattern = new RegExp(
  `^/[^/]+/subclan/[^/]+(?:/(?:boards/[^/]+|${redesignedSubclanTabPattern}))?/?$`
);
const customSubclanWorkspacePattern = new RegExp(
  `^/subclan/[^/]+(?:/(?:boards/[^/]+|${redesignedSubclanTabPattern}))?/?$`
);
const clanUtilityWorkspacePattern =
  /^\/(?:(?:notifications|invite|members|files|polls|mp3|event|forum|post|users|site_members)(?:\/|$)|u\/[^/]+\/posts\/[^/]+(?:\/|$)|video\/(?:list\/(?:all|user\/[^/]+)|add)|videos\/my|subclan_messages\/(?:list|member|create)|messages\/message\/[^/]+)(?:.*)?$/;
const tenantClanUtilityWorkspacePattern =
  /^\/[^/]+\/(?:(?:notifications|invite|members|files|polls|mp3|event|forum|post|users|site_members)(?:\/|$)|u\/[^/]+\/posts\/[^/]+(?:\/|$)|video\/(?:list\/(?:all|user\/[^/]+)|add)|videos\/my|subclan_messages\/(?:list|member|create)|messages\/message\/[^/]+)(?:.*)?$/;

export function playAudioSafely(source) {
  try {
    const playback = new Audio(source).play();
    if (playback && typeof playback.catch === "function") {
      playback.catch(() => {});
    }
  } catch (_error) {
    // Audio is best-effort; browsers may block playback before interaction.
  }
}

function playNotificationSound() {
  playAudioSafely(chat_notification);
}

function playNotificationSound2() {
  playAudioSafely(notification_sweet);
}

function realtimePairKey(kind, value) {
  return `${kind}:${`${value || "unknown"}`.trim() || "unknown"}`;
}

function notificationPairId(notification, kind) {
  if (kind === "call") {
    return (
      notification?.templateVars?.callId ||
      notification?.callId ||
      `${notification?.click || notification?.path || ""}`.split("/").filter(Boolean).pop()
    );
  }

  return (
    notification?.templateVars?.conversationId ||
    notification?.conversation ||
    notification?.chat ||
    `${notification?.click || notification?.path || ""}`.split("/").filter(Boolean).pop()
  );
}

function messagePairId(message) {
  return message?.conversation || message?.chat?._id || message?.chat;
}

/**
 * The API persists a notification and then relays the richer message/call
 * event. Pair those transports so the normal path makes one sound, while the
 * notification still provides a fallback when the richer relay is missed.
 */
export function createRealtimeSoundCoordinator({
  fallbackDelay = 500,
  pairWindow = 10000,
  setTimer = setTimeout,
  clearTimer = clearTimeout,
} = {}) {
  const pendingNotifications = new Map();
  const recentRealtimeEvents = new Map();

  const removeEntry = (collection, key, entry) => {
    const queue = collection.get(key) || [];
    const next = queue.filter((candidate) => candidate !== entry);
    if (next.length) collection.set(key, next);
    else collection.delete(key);
  };

  const shiftEntry = (collection, key) => {
    const queue = collection.get(key) || [];
    const entry = queue.shift();
    if (queue.length) collection.set(key, queue);
    else collection.delete(key);
    return entry;
  };

  const clearEntry = (entry) => {
    if (!entry) return;
    if (entry.fallbackTimer) clearTimer(entry.fallbackTimer);
    if (entry.expiryTimer) clearTimer(entry.expiryTimer);
  };

  const rememberRealtimeEvent = (key) => {
    const entry = {};
    entry.expiryTimer = setTimer(() => {
      removeEntry(recentRealtimeEvents, key, entry);
    }, pairWindow);
    recentRealtimeEvents.set(key, [
      ...(recentRealtimeEvents.get(key) || []),
      entry,
    ]);
  };

  const notification = (kind, id, playFallback) => {
    const key = realtimePairKey(kind, id);
    const recentEntry = shiftEntry(recentRealtimeEvents, key);
    if (recentEntry) {
      clearEntry(recentEntry);
      return;
    }

    const entry = { played: false };
    entry.fallbackTimer = setTimer(() => {
      entry.played = true;
      playFallback();
    }, fallbackDelay);
    entry.expiryTimer = setTimer(() => {
      removeEntry(pendingNotifications, key, entry);
    }, pairWindow);
    pendingNotifications.set(key, [
      ...(pendingNotifications.get(key) || []),
      entry,
    ]);
  };

  const realtime = (kind, id, playRealtime, { alwaysPlay = false } = {}) => {
    const key = realtimePairKey(kind, id);
    const pendingEntry = shiftEntry(pendingNotifications, key);
    if (pendingEntry) {
      clearEntry(pendingEntry);
      if (alwaysPlay || !pendingEntry.played) playRealtime();
      return;
    }

    playRealtime();
    rememberRealtimeEvent(key);
  };

  const cleanup = () => {
    [...pendingNotifications.values(), ...recentRealtimeEvents.values()]
      .flat()
      .forEach(clearEntry);
    pendingNotifications.clear();
    recentRealtimeEvents.clear();
  };

  return { notification, realtime, cleanup };
}

function getEntityDisplayName(entity) {
  if (!entity) return "User";
  const displayName =
    `${entity.displayName || entity?.custom_fields?.displayName || ""}`.trim();
  if (displayName) return displayName;
  const firstName = entity.first_name || entity.firstName || "";
  const lastName = entity.last_name || entity.lastName || "";
  return `${firstName} ${lastName}`.trim() || entity.username || "User";
}

function invalidateClanNotificationStream(stream) {
  queryClient.invalidateQueries({
    predicate: (query) =>
      Array.isArray(query.queryKey) &&
      query.queryKey[0] === "clan-redesign-notification-count" &&
      query.queryKey[3] === stream,
  });

  if (stream === NOTIFICATION_STREAMS.alerts) {
    queryClient.invalidateQueries({ queryKey: ["clan-redesign-notifications"] });
  } else if (stream === NOTIFICATION_STREAMS.bonds) {
    queryClient.invalidateQueries({ queryKey: ["clan-redesign-bond-notifications"] });
    queryClient.invalidateQueries({ queryKey: ["clan-redesign-bond-requests"] });
    queryClient.invalidateQueries({ queryKey: ["clan-redesign-current-bonds"] });
  } else if (stream === NOTIFICATION_STREAMS.events) {
    queryClient.invalidateQueries({
      predicate: (query) =>
        Array.isArray(query.queryKey) &&
        query.queryKey[0] === "clan-redesign-workspace-calendar",
    });
  } else if (stream === NOTIFICATION_STREAMS.conversations) {
    queryClient.invalidateQueries({ queryKey: ["clan-redesign-conversations"] });
  }
}

export default function IsLoggedIn({ user, children = null }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const authIdentity =
    `${user?.userinfo?._id || user?.userinfo?.guid || user?.userinfo?.username || user?.userinfo?.email || user?.userinfo?.mobile || "anon"}`.trim() ||
    "anon";
  const onlineUsers = useSelector((state) => state.soketSlice?.onlineUsers || []);
  const customTenantHost = isCustomTenantHost();
  // Every account view (overview, /account/todo, /account/activity, ...) owns
  // its own header and bottom nav via pages/account/AccountShell.jsx, so the
  // global chrome stays suppressed across the whole area, not just its root.
  const isAccountRoute = /^\/account(?:\/[^/]+)*\/?$/.test(location.pathname);
  const isTenantAccountRoute = /^\/[^/]+\/account(?:\/[^/]+)*\/?$/.test(
    location.pathname
  );
  const isTenantWorkspaceRoute = /^\/[^/]+(?:\/boards\/[^/]+)?\/?$/.test(location.pathname);
  const isCustomTenantWorkspaceRoute =
    customTenantHost && /^\/(?:home|boards\/[^/]+)?\/?$/.test(location.pathname);
  const isTenantSubclanWorkspaceRoute = tenantSubclanWorkspacePattern.test(
    location.pathname
  );
  const isCustomTenantSubclanWorkspaceRoute =
    customTenantHost && customSubclanWorkspacePattern.test(location.pathname);
  const isWorkspaceShellSectionRoute =
    tenantWorkspaceShellSectionPattern.test(location.pathname);
  const isCustomWorkspaceShellSectionRoute =
    customTenantHost &&
    customWorkspaceShellSectionPattern.test(location.pathname);
  const isTenantCommerceWorkspaceRoute = tenantCommerceWorkspacePattern.test(
    location.pathname
  );
  const isCustomTenantCommerceWorkspaceRoute =
    customTenantHost && customCommerceWorkspacePattern.test(location.pathname);
  const isClanUtilityWorkspaceRoute = clanUtilityWorkspacePattern.test(
    location.pathname
  );
  const isTenantClanUtilityWorkspaceRoute =
    tenantClanUtilityWorkspacePattern.test(location.pathname);
  const isCustomTenantClanUtilityWorkspaceRoute =
    customTenantHost && isClanUtilityWorkspaceRoute;
  const profileWorkspacePattern = /^\/(?:profile(?:\/[^/]+)?|u\/[^/]+(?:\/[^/]+)?)(?:\/|$)/;
  const tenantProfileWorkspacePattern = /^\/[^/]+\/(?:profile(?:\/[^/]+)?|u\/[^/]+(?:\/[^/]+)?)(?:\/|$)/;
  const isProfileWorkspaceRoute = profileWorkspacePattern.test(location.pathname);
  const isTenantProfileWorkspaceRoute = tenantProfileWorkspacePattern.test(
    location.pathname
  );
  const isCustomTenantProfileWorkspaceRoute =
    customTenantHost && isProfileWorkspaceRoute;
  const isTenantMessagesRoute = /^\/[^/]+\/messages(?:\/[^/]+)?(?:\/[^/]+)?\/?$/.test(
    location.pathname
  );
  const isCustomTenantMessagesRoute =
    customTenantHost && /^\/messages(?:\/[^/]+)?(?:\/[^/]+)?\/?$/.test(
      location.pathname
    );
  const isTenantCalibrationAdminRoute = /^\/[^/]+\/admin(?:\/|$)/.test(
    location.pathname
  );
  const isCustomTenantCalibrationAdminRoute =
    customTenantHost && /^\/admin(?:\/|$)/.test(location.pathname);
  const hideGlobalChrome =
    isAccountRoute ||
    isTenantAccountRoute ||
    isTenantWorkspaceRoute ||
    isCustomTenantWorkspaceRoute ||
    isTenantSubclanWorkspaceRoute ||
    isCustomTenantSubclanWorkspaceRoute ||
    isWorkspaceShellSectionRoute ||
    isCustomWorkspaceShellSectionRoute ||
    isTenantCommerceWorkspaceRoute ||
    isCustomTenantCommerceWorkspaceRoute ||
    isClanUtilityWorkspaceRoute ||
    isTenantClanUtilityWorkspaceRoute ||
    isCustomTenantClanUtilityWorkspaceRoute ||
    isProfileWorkspaceRoute ||
    isTenantProfileWorkspaceRoute ||
    isCustomTenantProfileWorkspaceRoute ||
    isTenantMessagesRoute ||
    isCustomTenantMessagesRoute ||
    isTenantCalibrationAdminRoute ||
    isCustomTenantCalibrationAdminRoute;
  const hideFloatingChat =
    isTenantCommerceWorkspaceRoute || isCustomTenantCommerceWorkspaceRoute;

  const {
    data: restoredSession,
    isLoading: isRestoringSession,
  } = useQuery({
    queryKey: ["route-auth-bootstrap", location.pathname, authIdentity],
    queryFn: async () => {
      const response = await get("/api/v1/users/bootstrap");
      return response?.data || response || {};
    },
    enabled: !user?.userinfo,
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: "always",
  });

  useEffect(() => {
    if (user?.userinfo || !restoredSession?.user) return;
    dispatch(updateUserInfo(restoredSession.user));
  }, [dispatch, restoredSession?.user, user?.userinfo]);

  const handleLogout = () => {
    (async () => {
      try {
        await axios.post(
          apiUrl("/api/v1/auth/logout"),
          {},
          { withCredentials: true }
        );
      } catch (_error) {
        // Best-effort logout: still clear the local session below.
      } finally {
        dispatch(logout());
        queryClient.clear();
        if (socket?.connected) {
          socket.disconnect();
        }
        navigate(buildTenantRelativePath("/login"), { replace: true });
      }
    })();
  };

  // ── Ringtone (looping ring for incoming calls) ─────────────────────
  const ringtoneRef = useRef(null);
  const [incomingCall, setIncomingCall] = useState(null); // { callId, roomId, type, caller }

  const stopRingtone = useCallback(() => {
    if (ringtoneRef.current) {
      ringtoneRef.current.stop();
      ringtoneRef.current = null;
    }
  }, []);

  const startRingtone = useCallback(() => {
    stopRingtone();
    ringtoneRef.current = createRingtone();
    ringtoneRef.current.start();
  }, [stopRingtone]);

  const signedInUserId = `${user?.userinfo?._id || ""}`;
  const signedInUsername = user?.userinfo?.username || "";
  const signedInFirstName = user?.userinfo?.first_name || "";
  const signedInLastName = user?.userinfo?.last_name || "";
  const signedInPhoto = user?.userinfo?.photo || "";
  const latestUserRef = useRef(user?.userinfo);
  latestUserRef.current = user?.userinfo;

  // Read through a ref so a cosmetic profile change (display name, avatar)
  // never lands in the connection effect's dependency list — rebuilding the
  // socket for one is what silently killed every consumer's subscription.
  const socketSetupRef = useRef(null);
  socketSetupRef.current = {
    userId: signedInUserId,
    info: {
      id: signedInUserId,
      username: signedInUsername,
      first_name: signedInFirstName,
      last_name: signedInLastName,
      photo: signedInPhoto,
    },
  };

  useEffect(() => {
    if (!signedInUserId) return undefined;

    const activeSocket = io(backendUrl);
    attachSocketInstance(activeSocket);
    const soundCoordinator = createRealtimeSoundCoordinator();

    // Emit setup on every (re)connection so the server registers this
    // user as online even after server restarts / deploys.
    const emitSetup = () => {
      activeSocket.emit("setup", socketSetupRef.current);
    };

    const handleOnlineUser = ({ type, info }) => {
      dispatch(setOnlineUsers({ type, info }));
    };

    const handleNewNotification = ({ notification }) => {
      const notificationStream = getNotificationStream(notification);
      const notificationType = `${notification?.type || ""}`.toLowerCase();
      const directMessageNotification =
        notificationStream === NOTIFICATION_STREAMS.conversations &&
        notificationType === "message";
      const callNotification =
        notificationStream === NOTIFICATION_STREAMS.conversations &&
        notificationType === "call";

      if (directMessageNotification) {
        soundCoordinator.notification(
          "message",
          notificationPairId(notification, "message"),
          playNotificationSound
        );
      } else if (callNotification) {
        soundCoordinator.notification(
          "call",
          notificationPairId(notification, "call"),
          playNotificationSound2
        );
      } else {
        playNotificationSound2();
      }
      invalidateClanNotificationStream(notificationStream);

      if (notificationStream === NOTIFICATION_STREAMS.alerts) {
        dispatch(
          reciveNoti({
            type: "notification",
          })
        );
      } else if (notificationType === "call") {
        dispatch(
          reciveNoti({
            type: "message",
          })
        );
      }

      // If this is a friend request, also bump the friend-requests badge
      if (notificationType === "friend_request") {
        const currentCount =
          latestUserRef.current?.recivedRequestsCount || 0;
        dispatch(updateNStats({ recivedRequestsCount: currentCount + 1 }));
      }

      if (!directMessageNotification) {
        toast.custom(
          (t) => (
            <Notification t={t} toast={toast} notification={notification} />
          ),
          { duration: 8000 }
        );
      }

      if (notificationStream === NOTIFICATION_STREAMS.alerts) {
        queryClient.setQueryData(["getNotifications"], (oldData) => {
          if (!oldData) return oldData;
          const notifications = Array.isArray(oldData?.data?.notifications)
            ? oldData.data.notifications
            : [];
          return {
            ...oldData,
            data: {
              ...(oldData.data || {}),
              notifications: [notification, ...notifications],
            },
          };
        });
      }
    };

    // When a friend request is accepted, add the new friend to online users
    const handleFriendAccepted = ({ friendInfo }) => {
      if (friendInfo) {
        dispatch(setOnlineUsers({ type: "add", info: friendInfo }));
      }
      invalidateClanNotificationStream(NOTIFICATION_STREAMS.bonds);
    };

    const handleMessageReceived = (newMessage) => {
      // Route to any open mini chat for this sender
      const senderId = String(
        newMessage?.sender?._id || newMessage?.sender?.id || ""
      );
      const hasMiniChat = miniChatBus.hasChatForSender(senderId);
      if (!hasMiniChat && newMessage?.sender) {
        miniChatBus.queueMessage(newMessage);
        miniChatBus.openChat(newMessage.sender);
      }
      miniChatBus.emit(newMessage);

      soundCoordinator.realtime(
        "message",
        messagePairId(newMessage),
        playNotificationSound
      );
      invalidateClanNotificationStream(NOTIFICATION_STREAMS.conversations);
      dispatch(
        reciveNoti({
          type: "message",
        })
      );

      // An open MiniChat already renders the incoming message, so only the
      // visual toast remains conditional. Sound and unread state never are.
      if (
        !hasMiniChat &&
        window.location.pathname.split("/").pop() !== `${newMessage?.chat || ""}`
      ) {
        toast.custom((t) => (
          <Notification
            t={t}
            toast={toast}
            notification={{
              sender: newMessage.sender,
              type: "message",
              click: buildTenantRelativePath(
                `/messages/message/${newMessage.sender?.username || ""}`
              ),
              content: `${getEntityDisplayName(newMessage.sender)} sent you a message`,
              createdAt: newMessage.createdAt,
            }}
          />
        ), { duration: 8000 });
      }
      updateMessages(newMessage);
    };

    const handleCustomizeChat = ({ type, content, chat }) => {
      if (type === "theme") {
        updateChatTheme(content, chat);
      }
      if (type === "name") {
        updateChatName(content, chat);
      }
    };

    const handleSeen = ({ message: newMessage, chat }) => {
      updateSeenMessages(newMessage, chat);
    };

    const handleIncomingCall = (callData) => {
      soundCoordinator.realtime(
        "call",
        callData?.callId,
        startRingtone,
        { alwaysPlay: true }
      );
      setIncomingCall(callData);
    };

    const handleCallEnded = () => {
      stopRingtone();
      setIncomingCall(null);
    };

    const handleCallRejected = () => {
      stopRingtone();
      toast.error("Call was rejected");
    };

    activeSocket.on("connect", emitSetup);
    activeSocket.on("online_user", handleOnlineUser);
    activeSocket.on("new_notification", handleNewNotification);
    activeSocket.on("friend_accepted", handleFriendAccepted);
    activeSocket.on("message_recieved", handleMessageReceived);
    activeSocket.on("customize_chat", handleCustomizeChat);
    activeSocket.on("seen", handleSeen);
    activeSocket.on("incoming_call", handleIncomingCall);
    activeSocket.on("call_ended", handleCallEnded);
    activeSocket.on("call_rejected", handleCallRejected);

    return () => {
      activeSocket.off("connect", emitSetup);
      activeSocket.off("online_user", handleOnlineUser);
      activeSocket.off("new_notification", handleNewNotification);
      activeSocket.off("friend_accepted", handleFriendAccepted);
      activeSocket.off("message_recieved", handleMessageReceived);
      activeSocket.off("customize_chat", handleCustomizeChat);
      activeSocket.off("seen", handleSeen);
      activeSocket.off("incoming_call", handleIncomingCall);
      activeSocket.off("call_ended", handleCallEnded);
      activeSocket.off("call_rejected", handleCallRejected);
      soundCoordinator.cleanup();
      stopRingtone();
      attachSocketInstance(null);
      activeSocket.disconnect();
    };
  }, [dispatch, signedInUserId, startRingtone, stopRingtone]);

  // Presence info the server holds is refreshed in place on a profile edit;
  // the connection itself is left alone. `connect` covers the initial emit.
  useEffect(() => {
    if (!signedInUserId || !socket.connected) return;
    socket.emit("setup", socketSetupRef.current);
  }, [
    signedInFirstName,
    signedInLastName,
    signedInPhoto,
    signedInUserId,
    signedInUsername,
  ]);

  // Stop ringtone whenever incomingCall is cleared (safety net)
  useEffect(() => {
    if (!incomingCall) stopRingtone();
  }, [incomingCall, stopRingtone]);

  // Cleanup ringtone on unmount
  useEffect(() => {
    return () => stopRingtone();
  }, [stopRingtone]);

  // ── Dropdown data ──────────────────────────────────────────────────
  const [notiItems, setNotiItems] = useState(null);   // null = not loaded yet
  const [frReqItems, setFrReqItems] = useState(null); // null = not loaded yet
  const [msgItems, setMsgItems] = useState(null);     // null = not loaded yet

  // Eagerly fetch notifications and friend requests on mount so data
  // is ready before the user clicks the dropdown icons.
  useEffect(() => {
    if (!user.userinfo) return;
    axios
      .get(apiUrl("/api/v1/notifications?stream=alerts&page=1&limit=5"), { withCredentials: true })
      .then((res) => setNotiItems(res.data?.data?.notifications || []))
      .catch(() => setNotiItems([]));
    axios
      .get(apiUrl("/api/v1/friends/requests?page=1&limit=5"), { withCredentials: true })
      .then((res) => setFrReqItems(res.data?.data?.requests || []))
      .catch(() => setFrReqItems([]));
    axios
      .get(apiUrl("/api/v1/messages/conversations"), { withCredentials: true })
      .then((res) => setMsgItems((res.data?.data?.conversations || []).slice(0, 5)))
      .catch(() => setMsgItems([]));
  }, [user.userinfo]);

  // Re-fetch when a dropdown is opened (to pick up new items)
  const handleOpenBox = useCallback(
    async (type) => {
      if (type === "notifications") {
        try {
          const res = await axios.get(
            apiUrl("/api/v1/notifications?stream=alerts&page=1&limit=5"),
            { withCredentials: true }
          );
          setNotiItems(res.data?.data?.notifications || []);
        } catch {
          // best-effort
        }
      }
      if (type === "friends") {
        try {
          const res = await axios.get(
            apiUrl("/api/v1/friends/requests?page=1&limit=5"),
            { withCredentials: true }
          );
          setFrReqItems(res.data?.data?.requests || []);
        } catch {
          // best-effort
        }
      }
      if (type === "messages") {
        try {
          const res = await axios.get(
            apiUrl("/api/v1/messages/conversations"),
            { withCredentials: true }
          );
          setMsgItems((res.data?.data?.conversations || []).slice(0, 5));
        } catch {
          // best-effort
        }
      }
    },
    []
  );

  // ── Accept / Reject friend request from dropdown ─────────────────
  const handleAcceptRequest = useCallback(
    async (senderId) => {
      try {
        await axios.post(
          apiUrl(`/api/v1/friends/accept/${senderId}`),
          {},
          { withCredentials: true }
        );
        // Remove from local list
        setFrReqItems((prev) => (prev || []).filter((r) => {
          const s = r.requester || r.sender || {};
          return String(s._id) !== String(senderId);
        }));
        // Decrement badge count in Redux
        const currentCount = user.userinfo?.recivedRequestsCount || 0;
        dispatch(updateNStats({ recivedRequestsCount: Math.max(0, currentCount - 1) }));
        toast.success("Friend request accepted");
      } catch (err) {
        toast.error(err?.response?.data?.message || "Failed to accept request");
      }
    },
    [dispatch, user.userinfo?.recivedRequestsCount]
  );

  const handleRejectRequest = useCallback(
    async (senderId) => {
      try {
        await axios.delete(
          apiUrl(`/api/v1/friends/reject/${senderId}`),
          { withCredentials: true }
        );
        // Remove from local list
        setFrReqItems((prev) => (prev || []).filter((r) => {
          const s = r.requester || r.sender || {};
          return String(s._id) !== String(senderId);
        }));
        // Decrement badge count in Redux
        const currentCount = user.userinfo?.recivedRequestsCount || 0;
        dispatch(updateNStats({ recivedRequestsCount: Math.max(0, currentCount - 1) }));
        toast.success("Friend request rejected");
      } catch (err) {
        toast.error(err?.response?.data?.message || "Failed to reject request");
      }
    },
    [dispatch, user.userinfo?.recivedRequestsCount]
  );

  // ── Incoming call handlers ──────────────────────────────────────
  const handleAcceptCall = useCallback(
    async () => {
      if (!incomingCall?.callId) return;
      stopRingtone();
      try {
        await axios.post(apiUrl(`/api/v1/calls/${incomingCall.callId}/accept`), {}, { withCredentials: true });
        setIncomingCall(null);
        const returnTo =
          typeof window !== "undefined"
            ? `${window.location.pathname || ""}${window.location.search || ""}`
            : "";
        navigate(buildTenantRelativePath(`/call/${incomingCall.callId}`), {
          state: { returnTo: returnTo || buildTenantRelativePath("/") },
        });
      } catch (err) {
        toast.error(err?.response?.data?.message || "Failed to accept call");
        setIncomingCall(null);
      }
    },
    [incomingCall, navigate, stopRingtone]
  );

  const handleRejectCall = useCallback(
    async () => {
      if (!incomingCall?.callId) return;
      stopRingtone();
      try {
        await axios.post(apiUrl(`/api/v1/calls/${incomingCall.callId}/reject`), {}, { withCredentials: true });
      } catch (_) {}
      setIncomingCall(null);
    },
    [incomingCall, stopRingtone]
  );

  // ── Render dropdown items ────────────────────────────────────────
  const renderNotiItem = (n) => {
    const actor = n.actor || n.sender || {};
    const name = getEntityDisplayName(actor).trim() || "Someone";
    const photo = actor.photo || "/images/default_pic.png";
    const text = n.message || n.content || `${name} sent you a notification`;
    const link = n.click || buildTenantRelativePath("/notifications/all");
    return (
      <li key={n._id} style={{ borderBottom: "1px solid #eee" }}>
        <Link
          to={link}
          style={{ display: "flex", alignItems: "center", padding: "8px 12px", textDecoration: "none", color: "#333", gap: 8 }}
        >
          <img src={photo} alt="" style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover" }} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/images/default_pic.png"; }} />
          <div style={{ fontSize: 13, lineHeight: 1.3 }}>{text}</div>
        </Link>
      </li>
    );
  };

  const notificationsDropdown =
    notiItems === null ? (
      <div style={{ padding: "10px 12px", color: "#999", fontSize: 13 }}>Loading...</div>
    ) : notiItems.length > 0 ? (
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {notiItems.map(renderNotiItem)}
      </ul>
    ) : null;

  const friendsDropdown =
    frReqItems === null ? (
      <div style={{ padding: "10px 12px", color: "#999", fontSize: 13 }}>Loading...</div>
    ) : frReqItems.length > 0 ? (
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {frReqItems.map((r) => {
          const s = r.requester || r.sender || {};
          const senderId = s._id;
          const name = getEntityDisplayName(s).trim() || "Someone";
          const photo = s.photo || "/images/default_pic.png";
          return (
            <li key={r._id} style={{ borderBottom: "1px solid #eee", padding: "8px 12px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Link to={buildTenantUserPath(s.username, "", "#")} style={{ display: "flex", alignItems: "center", gap: 8, textDecoration: "none", color: "#333", flex: 1, minWidth: 0 }}>
                  <img src={photo} alt="" style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/images/default_pic.png"; }} />
                  <div style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</div>
                </Link>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleAcceptRequest(senderId); }}
                    title="Accept"
                    style={{
                      width: 32, height: 32, borderRadius: "50%", border: "none",
                      backgroundColor: "#4caf50", color: "#fff", cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 16, fontWeight: 700, lineHeight: 1,
                    }}
                  >
                    ✓
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleRejectRequest(senderId); }}
                    title="Reject"
                    style={{
                      width: 32, height: 32, borderRadius: "50%", border: "none",
                      backgroundColor: "#e53935", color: "#fff", cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 16, fontWeight: 700, lineHeight: 1,
                    }}
                  >
                    ✕
                  </button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    ) : null;

  const messagesDropdown =
    msgItems === null ? (
      <div style={{ padding: "10px 12px", color: "#999", fontSize: 13 }}>Loading...</div>
    ) : msgItems.length > 0 ? (
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {msgItems.map((c) => {
          // MERN conversations API: participants[] has current user + partner(s)
          const myId = String(user.userinfo?._id || "");
          const other = (c.participants || []).find((p) => String(p._id) !== myId) || c.participants?.[0] || {};
          const name = getEntityDisplayName(other).trim() || c.name || "Chat";
          const photo = other.photo || "/images/default_pic.png";
          const lastMsg = c.lastMessage;
          const snippet = lastMsg?.content
            ? (lastMsg.content.length > 40 ? lastMsg.content.slice(0, 40) + "…" : lastMsg.content)
            : "";
          const link = buildTenantRelativePath(
            `/messages/message/${other.username || c._id}`
          );
          return (
            <li key={c._id} style={{ borderBottom: "1px solid #eee" }}>
              <Link
                to={link}
                style={{ display: "flex", alignItems: "center", padding: "8px 12px", textDecoration: "none", color: "#333", gap: 8 }}
              >
                <img src={photo} alt="" style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</div>
                  {snippet && (
                    <div style={{ fontSize: 12, color: "#777", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 1 }}>{snippet}</div>
                  )}
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
    ) : null;

  if (!user.userinfo && (isRestoringSession || restoredSession?.user)) {
    return <Loading />;
  }

  return user.userinfo ? (
    <MiniChatProvider user={user.userinfo}>
      {hideGlobalChrome ? null : (
        <>
          <Topbar user={user.userinfo} onLogout={handleLogout} />
          <SubNavbar
            user={user.userinfo}
            friendRequests={user.userinfo?.recivedRequestsCount || 0}
            messageCount={user.userinfo?.unseenMessages || 0}
            notifCount={user.userinfo?.unseenNotification || 0}
            onOpenBox={handleOpenBox}
            notificationsDropdown={notificationsDropdown}
            friendsDropdown={friendsDropdown}
            messagesDropdown={messagesDropdown}
          />
        </>
      )}
      <React.Suspense fallback={<Loading />}>
        {children || <Outlet />}
      </React.Suspense>
      {hideGlobalChrome ? null : (
        <>
          <Footer />
          <ChatSidebar onlineFriends={onlineUsers} />
        </>
      )}
      {hideFloatingChat ? null : <ChatPopupManager />}

      {/* ── Incoming call overlay ─────────────────────────────── */}
      {incomingCall && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.6)", zIndex: 10000,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {/* Pulse animation for accept button */}
          <style>{`
            @keyframes vds-pulse-ring {
              0% { box-shadow: 0 0 0 0 rgba(76,175,80,0.6); }
              70% { box-shadow: 0 0 0 16px rgba(76,175,80,0); }
              100% { box-shadow: 0 0 0 0 rgba(76,175,80,0); }
            }
            .vds-call-accept-btn {
              animation: vds-pulse-ring 1.5s ease-out infinite;
            }
          `}</style>
          <div style={{
            backgroundColor: "#fff", borderRadius: 16, padding: "32px 40px",
            textAlign: "center", minWidth: 300, boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          }}>
            <img
              src={incomingCall.caller?.photo || "/images/default_pic.png"}
              alt=""
              style={{ width: 80, height: 80, borderRadius: "50%", objectFit: "cover", marginBottom: 12 }}
            />
            <h3 style={{ margin: "0 0 4px", fontSize: 18 }}>
              {`${incomingCall.caller?.first_name || ""} ${incomingCall.caller?.last_name || ""}`.trim() || "Someone"}
            </h3>
            <p style={{ color: "#666", margin: "0 0 20px", fontSize: 14 }}>
              Incoming {incomingCall.type === "audio" ? "voice" : "video"} call...
            </p>
            <div style={{ display: "flex", justifyContent: "center", gap: 24 }}>
              <button
                onClick={handleRejectCall}
                style={{
                  width: 56, height: 56, borderRadius: "50%", border: "none",
                  backgroundColor: "#e53935", color: "#fff", fontSize: 22,
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                }}
                title="Reject"
              >
                <i className="fa-solid fa-phone-slash"></i>
              </button>
              <button
                className="vds-call-accept-btn"
                onClick={handleAcceptCall}
                style={{
                  width: 56, height: 56, borderRadius: "50%", border: "none",
                  backgroundColor: "#4caf50", color: "#fff", fontSize: 22,
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                }}
                title="Accept"
              >
                <i className="fa-solid fa-phone"></i>
              </button>
            </div>
          </div>
        </div>
      )}
    </MiniChatProvider>
  ) : (
    <Navigate to={buildTenantRelativePath("/login")} replace />
  );
}
